import React, { useEffect, useState } from 'react';
import {
  isBeforeDeviceCurrentMinute,
  PAST_FORECAST_TIME_ERROR,
  startOfLocalMinute,
  toLocalMinuteInputValue
} from '../../utils/forecastTimePermissions.js';

const ForecastConfirmationModal = ({
  open,
  onClose,
  onConfirm,
  defaultTime,
  defaultHours,
  isWarmerProcess = false,
  isAzucarGlassProcess = false,
  isSuperAdmin = false
}) => {
  const [timeValue, setTimeValue] = useState('');
  const [mode, setMode] = useState('lifespan');
  const [hoursValue, setHoursValue] = useState('');
  const [currentMinute, setCurrentMinute] = useState(() => startOfLocalMinute(new Date()));

  useEffect(() => {
    if (!open) {
      return;
    }
    const now = new Date();
    const requestedDefaultTime = defaultTime instanceof Date ? defaultTime : now;
    const safeDefaultTime = !isSuperAdmin && isBeforeDeviceCurrentMinute(requestedDefaultTime)
      ? now
      : requestedDefaultTime;
    setCurrentMinute(startOfLocalMinute(now));
    setTimeValue(toLocalMinuteInputValue(safeDefaultTime));
    if (isWarmerProcess) {
      setMode('lifespan');
      setHoursValue('');
      return;
    }
    if (isAzucarGlassProcess) {
      setMode('hours');
      if (typeof defaultHours === 'number' && !Number.isNaN(defaultHours)) {
        setHoursValue(String(defaultHours));
      } else {
        setHoursValue('24');
      }
      return;
    }
    if (typeof defaultHours === 'number' && !Number.isNaN(defaultHours)) {
      setMode('hours');
      setHoursValue(String(defaultHours));
    } else {
      setMode('lifespan');
      setHoursValue('');
    }
  }, [open, defaultTime, defaultHours, isWarmerProcess, isAzucarGlassProcess, isSuperAdmin]);

  useEffect(() => {
    if (!open || isSuperAdmin) {
      return undefined;
    }
    const refreshCurrentMinute = () => setCurrentMinute(startOfLocalMinute(new Date()));
    refreshCurrentMinute();
    const intervalId = setInterval(refreshCurrentMinute, 30000);
    return () => clearInterval(intervalId);
  }, [open, isSuperAdmin]);

  if (!open) {
    return null;
  }

  const resolvedMode = isWarmerProcess ? 'lifespan' : (isAzucarGlassProcess ? 'hours' : mode);
  const resolvedHours = Number.parseInt(hoursValue, 10);
  const parsedForecastTime = timeValue ? new Date(timeValue) : null;
  const hasPastForecastTime = !isSuperAdmin && isBeforeDeviceCurrentMinute(parsedForecastTime);
  const isGenerateDisabled = resolvedMode === 'hours'
    ? (!Number.isFinite(resolvedHours) || resolvedHours <= 0 || hasPastForecastTime)
    : hasPastForecastTime;
  const minTimeValue = !isSuperAdmin && currentMinute
    ? toLocalMinuteInputValue(currentMinute)
    : undefined;
  const handleConfirm = () => {
    if (hasPastForecastTime) {
      return;
    }
    onConfirm?.({
      forecastTime: timeValue,
      mode: resolvedMode,
      hoursValue: isWarmerProcess ? '' : hoursValue
    });
  };

  return (
    <div id="forecast-confirmation-modal" className="modal" style={{ display: 'block' }}>
      <div className="modal-content">
        <div className="modal-header">
          <h3>Confirmar Generación de Pronóstico</h3>
          <button className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">
          <p>Seleccione los parámetros para generar el pronóstico.</p>
          <div className="form-group">
            <label htmlFor="forecast-time-input" className="form-label">Hora del Pronóstico:</label>
            <input
              type="datetime-local"
              id="forecast-time-input"
              className="form-input"
              value={timeValue}
              min={minTimeValue}
              onChange={(event) => setTimeValue(event.target.value)}
            />
            {hasPastForecastTime ? (
              <p className="form-error" role="alert">{PAST_FORECAST_TIME_ERROR}</p>
            ) : null}
          </div>
          <div className="form-group">
            <label htmlFor="forecast-type-select" className="form-label">Calcular pronóstico basado en:</label>
            {isWarmerProcess ? (
              <select
                id="forecast-type-select"
                className="form-select"
                value="lifespan"
                disabled
              >
                <option value="lifespan">De acuerdo a tiempo de vida de warmer</option>
              </select>
            ) : isAzucarGlassProcess ? (
              <select
                id="forecast-type-select"
                className="form-select"
                value="hours"
                disabled
              >
                <option value="hours">Cantidad de horas</option>
              </select>
            ) : (
              <select
                id="forecast-type-select"
                className="form-select"
                value={mode}
                onChange={(event) => setMode(event.target.value)}
              >
                <option value="lifespan">Tiempos de vida de items</option>
                <option value="hours">Cantidad de horas</option>
              </select>
            )}
          </div>
          {resolvedMode === 'hours' && (
            <div className="form-group" id="forecast-hours-container">
              <label htmlFor="forecast-hours-input" className="form-label">Horas a pronosticar:</label>
              <input
                type="number"
                id="forecast-hours-input"
                className="form-input"
                min="1"
                max={isAzucarGlassProcess ? '24' : '12'}
                placeholder={isAzucarGlassProcess ? '1-24 horas' : '1-12 horas'}
                value={hoursValue}
                onChange={(event) => setHoursValue(event.target.value)}
              />
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>Cancelar</button>
          <button className="btn-primary" onClick={handleConfirm} disabled={isGenerateDisabled}>Generar</button>
        </div>
      </div>
    </div>
  );
};

export default ForecastConfirmationModal;
